/**
 * format — UI 전역에서 사용하는 포맷팅 유틸리티 모음
 *
 * 대시보드 통계, 테이블 셀, 상세 페이지 등에서 날짜·금액·숫자를
 * 한국어 로케일에 맞게 표시하기 위한 함수들.
 * date-fns의 ko locale을 사용하여 한국어 날짜 포맷을 지원한다.
 */

import { format as fnsFormat } from 'date-fns'
import { ko } from 'date-fns/locale'

/**
 * 날짜를 한국어 포맷으로 변환한다.
 * ISO 문자열과 Date 객체 모두 처리 가능.
 *
 * @param date - ISO 날짜 문자열 또는 Date 객체
 * @param pattern - date-fns 포맷 패턴 (기본: 'yyyy년 M월 d일')
 * @returns 포맷된 날짜 문자열
 *
 * @example
 *   formatDate('2026-03-15T10:00:00Z')        // '2026년 3월 15일'
 *   formatDate(new Date(), 'M월 d일 (EEEE)')  // '2월 26일 (수요일)'
 */
export function formatDate(date: string | Date, pattern = 'yyyy년 M월 d일'): string {
  const d = typeof date === 'string' ? new Date(date) : date
  return fnsFormat(d, pattern, { locale: ko })
}

/**
 * 숫자를 원화(₩) 형식으로 포맷한다.
 * 천 단위 콤마가 자동 적용된다.
 *
 * @example formatCurrency(1234567) // '₩1,234,567'
 */
export function formatCurrency(amount: number): string {
  return `₩${amount.toLocaleString('ko-KR')}`
}

/**
 * 숫자에 천 단위 콤마를 붙인다.
 *
 * @example formatNumber(1234567) // '1,234,567'
 */
export function formatNumber(n: number): string {
  return n.toLocaleString('ko-KR')
}

/**
 * AI 크레딧 수량을 '~개' 형식으로 표시한다.
 *
 * @example formatCredits(12) // '12개'
 */
export function formatCredits(n: number): string {
  return `${n.toLocaleString('ko-KR')}개`
}

/**
 * 소수점 첫째 자리까지 퍼센트 표시.
 *
 * @example formatPercent(45.23) // '45.2%'
 */
export function formatPercent(n: number): string {
  return `${n.toFixed(1)}%`
}
